import { NavLink } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { ChartCard } from "../components/ChartCard";
import { ChartTooltipContent } from "../components/ChartTooltipContent";
import { DataTableCard } from "../components/DataTableCard";
import { GroundedVisualStrip } from "../components/GroundedVisualStrip";
import { KpiRail } from "../components/KpiRail";
import { NumberedListCard } from "../components/NumberedListCard";
import { PremiumHero } from "../components/PremiumHero";
import { SignalPill } from "../components/SignalPill";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";

type ConceptProgramPageData = Awaited<ReturnType<typeof loadConceptProgramPage>>;

async function loadConceptProgramPage() {
  const [concept, site, demand, comps, precedent] = await Promise.all([
    api.getConcept(),
    api.getSite(),
    api.getDemand(),
    api.getComps(),
    api.getPrecedent()
  ]);

  return { concept, site, demand, comps, precedent };
}

export function ConceptProgramPage() {
  const { data, loading, error } = useContent("concept-program-page", loadConceptProgramPage);

  if (!data) {
    return (
      <PageContainer
        eyebrow="Concept & Program"
        title="Ranked concept logic. One lead path."
        lede="This page should resolve the concept decision fast: one lead concept, visible alternatives, explicit site and market fit, and a compact sensitivity stack."
      >
        {loading ? <div className="loading-panel shimmer-card">Loading concept program...</div> : null}
        {error ? <div className="error-panel">{error}</div> : null}
      </PageContainer>
    );
  }

  const { concept, site, demand, comps, precedent } = data;
  const rankChartData = concept.conceptComparison.conceptOptions.map((item, index) => ({
    name: item.concept.replace("Single Premium Compact Modern Home", "Lead concept"),
    value: concept.conceptComparison.conceptOptions.length - index,
    label: item.currentRank
  }));
  const comparisonRows = concept.conceptComparison.conceptOptions.map((item) => [
    `${item.concept} (${item.currentRank})`,
    item.whyItFits,
    item.mainRisk
  ]);
  const alignmentRows = [
    ["Site fit", site.siteSummary.leadConceptFit, site.siteSummary.overallPosture],
    ["Demand fit", demand.currentDemandRead, demand.locationOverview],
    ["Comp fit", comps.compsSummary.currentCompRead, concept.conceptSummary.leadConcept.currentCompSupport],
    ["Precedent fit", precedent.currentInterpretation, precedent.overview]
  ];
  const sensitivityItems = [...concept.conceptSummary.tests, ...concept.conceptComparison.limitations].slice(0, 5);

  return (
    <PageContainer
      eyebrow="Concept & Program"
      title="Ranked concept logic. One lead path."
      lede="The selection case should clear at a glance: the lead concept is visibly ahead, alternatives remain visible, and the remaining sensitivities are finite."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading concept program...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      <div className="stack-xl">
        <PremiumHero
          eyebrow="Program Decision"
          title="One concept is still clearly ahead."
          subtitle="The lead concept wins because it fits the parcel, matches the market, and keeps the execution stack cleaner than the alternatives. The alternatives stay visible, but they are not tied for first."
          backgroundImage="/comps/104-f-st-map.jpg"
          backgroundLabel="Grounded local context imagery"
          badges={
            <>
              <SignalPill label={concept.conceptSummary.status} tone="positive" />
              <SignalPill label={concept.conceptSummary.leadConcept.concept} tone="neutral" />
              <SignalPill label={`${sensitivityItems.length} open sensitivities`} tone="warning" />
            </>
          }
          actions={
            <>
              <NavLink to="/market-support" className="link-button link-button-primary">
                Open Market Support
              </NavLink>
              <NavLink to="/site-zoning" className="link-button link-button-secondary">
                Open Site & Zoning
              </NavLink>
            </>
          }
          aside={
            <div className="stack-md">
              <div className="stack-xs">
                <span className="card-eyebrow">At a Glance</span>
                <div style={{ fontSize: "1.8rem", lineHeight: 1, color: "#f6fbff", letterSpacing: "-0.04em" }}>
                  {concept.conceptSummary.leadConcept.concept}
                </div>
                <p style={{ margin: 0, color: "rgba(223,233,248,0.78)", lineHeight: 1.5 }}>
                  {concept.conceptSummary.leadConcept.corePositioning}
                </p>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "12px"
                }}
              >
                {[
                  ["Concept options", `${concept.conceptComparison.conceptOptions.length}`],
                  ["Positioning paths", `${concept.conceptComparison.positioningOptions.length}`],
                  ["Site posture", site.siteSummary.overallPosture],
                  ["Market read", demand.currentDemandRead]
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      borderRadius: "18px",
                      padding: "14px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)"
                    }}
                  >
                    <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(198,211,234,0.62)" }}>
                      {label}
                    </div>
                    <div style={{ marginTop: "8px", color: "#f4f8ff", lineHeight: 1.35 }}>{value}</div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  overflow: "hidden",
                  borderRadius: "18px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.04)"
                }}
              >
                <img src="/comps/110-f-st-streetview.jpg" alt="Nearby built-product context relevant to concept positioning" style={{ display: "block", width: "100%", height: "160px", objectFit: "cover" }} />
              </div>
            </div>
          }
        />

        <KpiRail
          items={[
            { label: "Lead concept", value: concept.conceptSummary.leadConcept.concept, note: "Current first choice", tone: "positive" },
            { label: "Selection read", value: concept.conceptComparison.currentBestRead, note: "Why the ranking holds", tone: "neutral" },
            { label: "Positioning read", value: concept.conceptComparison.positioningBestRead, note: "Best current guest/product angle", tone: "neutral" },
            { label: "Open sensitivities", value: `${sensitivityItems.length}`, note: "Finite issues that could still move the stack", tone: "warning" }
          ]}
        />

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
          }}
        >
          <ChartCard
            eyebrow="Rank Shape"
            title="The ranking is not flat"
            description="This chart is a visual ranking ladder based on the current ordered concept stack. It shows one lead concept and subordinate alternatives, not equal candidates."
          >
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rankChartData} layout="vertical" margin={{ top: 8, right: 12, left: 8, bottom: 8 }}>
                  <CartesianGrid horizontal={false} stroke="rgba(255,255,255,0.08)" />
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#d7e4f8", fontSize: 12 }} axisLine={false} tickLine={false} width={110} />
                  <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltipContent title="Relative rank position" />} />
                  <Bar dataKey="value" name="Rank position" fill="#7fd0ff" radius={[0, 10, 10, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <NumberedListCard
            eyebrow="Why The Lead Concept Wins"
            title="Control and fit still beat novelty."
            items={concept.conceptSummary.whyLead.slice(0, 4)}
            tone="positive"
          />
        </div>

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
          }}
        >
          <DataTableCard
            eyebrow="Concept Ranking"
            title="Concept comparison"
            description="Alternatives remain visible, but the table makes the current ranking explicit instead of burying it in prose."
            columns={["Concept", "Why it fits", "Main risk"]}
            rows={comparisonRows}
          />

          <DataTableCard
            eyebrow="Site + Market Alignment"
            title="Why the lead concept still aligns"
            description="The program is not being selected in isolation. It is being carried because site and market signals reinforce it."
            columns={["Layer", "Current read", "Supporting signal"]}
            rows={alignmentRows}
          />
        </div>

        <GroundedVisualStrip
          eyebrow="Grounded Context"
          title="Local context supporting the current concept read"
          items={[
            {
              src: "/comps/104-f-st-streetview.jpg",
              alt: "Nearby built compact home context",
              label: "Built compact context",
              note: "Nearby compact product context supports the lead concept's physical plausibility."
            },
            {
              src: "/comps/110-f-st-streetview.jpg",
              alt: "Second nearby built context image",
              label: "Comparable build quality",
              note: "The concept choice is grounded in nearby built reality rather than a novelty program."
            },
            {
              src: "/comps/104-f-st-map.jpg",
              alt: "Local map context near subject area",
              label: "Local support radius",
              note: "The surrounding support set stays geographically tight enough to matter."
            },
            {
              src: "/comps/110-f-st-map.jpg",
              alt: "Second local map context",
              label: "Program context",
              note: "Map context reinforces the same urban fabric the concept is trying to fit."
            }
          ]}
        />

        <section
          style={{
            borderRadius: "24px",
            padding: "22px",
            background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
          }}
        >
          <div
            style={{
              display: "grid",
              gap: "18px",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
              alignItems: "start"
            }}
          >
            <div className="stack-xs">
              <span className="card-eyebrow">Remaining Program Sensitivities</span>
              <h3 className="card-title">What could still move the ranking</h3>
              <div style={{ color: "rgba(221, 231, 247, 0.78)", lineHeight: 1.55 }}>
                {sensitivityItems.map((item, index) => (
                  <div key={`${item}-${index}`} style={{ paddingTop: index === 0 ? 0 : "12px", borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="stack-xs">
              <span className="card-eyebrow">Supporting Detail</span>
              <h3 className="card-title">Use supporting routes after the selection logic is clear.</h3>
              <div className="row-wrap gap-sm">
                <NavLink to="/concept" className="link-button link-button-secondary">
                  Concept
                </NavLink>
                <NavLink to="/market-support" className="link-button link-button-secondary">
                  Market Support
                </NavLink>
                <NavLink to="/site-zoning" className="link-button link-button-secondary">
                  Site & Zoning
                </NavLink>
              </div>
            </div>
          </div>
        </section>
      </div>
    </PageContainer>
  );
}
